import { ref, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

export const supportedLocales = ["en", "fr", "de", "el"];
export const defaultLocale = "en";


// Global ref
export const currentLocale = ref(localStorage.getItem("locale") || defaultLocale);


export const locales = ref([
    { label: "English", value: "en" },
    { label: "German / Deutsch", value: "de" },
    { label: "Greek / Ελληνικά", value: "el" },
    { label: "French / Français", value: "fr" }
]);


export function fixLocaleInUrl(to, from, next) {

    let locale = to.params.locale;
    const savedLocale = localStorage.getItem("locale") || defaultLocale;

    if (!locale) {
        return next({ path: `/${savedLocale}${to.path}`, query: to.query });
    }

    if (!supportedLocales.includes(locale)) {
        return next({ path: `/${defaultLocale}${to.path.replace(/^\/[a-z]{2}/, "")}`, query: to.query });
    }

    if (savedLocale !== locale) {
        localStorage.setItem("locale", locale);
    }

    next();
}


export function useLocalization() {
    const router = useRouter();
    const route = useRoute();
    const i18n = useI18n();


    const getCurrentLocale = computed(() => {
        let locale = route.params.locale || localStorage.getItem("locale") || defaultLocale;
        if (!supportedLocales.includes(locale)) {
            locale = defaultLocale;
        }
        return locale;
    });


    const updateLocale = (newLocale: string) => {
        if (!supportedLocales.includes(newLocale)) {
            newLocale = defaultLocale;
        }

        i18n.locale.value = newLocale;
        localStorage.setItem("locale", newLocale);
        currentLocale.value = newLocale;

        let currentPath = route.path.replace(/^\/[a-z]{2}($|\/)/, "/");
        router.replace({ path: `/${newLocale}${currentPath}`, query: route.query });
    };
    

    const detectAndSetLocale = () => {

        let preferredLocale = localStorage.getItem("locale") || defaultLocale;

        if (!supportedLocales.includes(preferredLocale)) {
            preferredLocale = defaultLocale;
        }
        
        if (route.params.locale === preferredLocale) {
            return;
        }
        
        let newPath = route.fullPath.replace(/^\/[a-z]{2}($|\/)/, "/");
        
        router.replace({ path: `/${preferredLocale}${newPath}`, query: route.query });
    };
    
    watch(
        () => route.params.locale,
        (newLocale) => {
            if (newLocale && supportedLocales.includes(newLocale)) {
                updateLocale(newLocale);
            }
        },
        { immediate: true }
    );

    return {
        getCurrentLocale,
        updateLocale,
        detectAndSetLocale,
        currentLocale
    };
}


export function withLocale(route) {
    return {
        ...route,
        path: `/:locale(${supportedLocales.join("|")})${route.path === "/" ? "" : route.path}`,
    };
}